// Orchestrates the ESA conversational flow: optimistic message, API call,
// conversation rekey on first message, domain store updates.
// Phase 1: blocking HTTP. Track C will swap converse() for converseStream().

import { useCallback, useEffect, useRef, useState } from 'react';
import { useShellStore } from '../../../shell/store/shellStore';
import { useEsaDomainStore } from '../store/esaDomainStore';
import { getMessageCache } from '../cache/messageCacheInstance';
import { converse, createConversation } from '../services/esaApi';
import type { ESACacheMessage } from '../cache/MessageCache';
import { getOrCreateAwarenessStore } from '../../Canvas/store/canvasAwarenessStore';
import { dispatchInstruction } from './useInstructionDispatch';
import { useEsaAttachmentStore } from '../store/esaAttachmentStore';
import type { MessageAttachmentRef } from '../types';
import { syncCanvasAfterConverse } from '../../Canvas/notifyCanvasRefresh';
import { resolveComposerTextForOpenCommitment } from '../utils/openCommitment';

/**
 * Reads the canvas awareness store, flushes any debounced pending writes
 * (so the snapshot is always fresh), serializes to a plain object for the
 * converse context payload, and clears the one-shot dataResponse.
 *
 * WHY flush here (UC-4 + DR-4):
 * The user hit send — we want the absolute latest canvas state, even if the
 * 500ms/300ms debounce timers haven't fired yet. flush() commits them synchronously.
 *
 * WHY clear dataResponse (UC-5):
 * A data response is a one-shot reply to a DATA_REQUEST instruction. Once ESA
 * has seen it (in this converse call) it should not be resent in subsequent turns.
 */
function buildAwarenessContext(): Record<string, unknown> | undefined {
  try {
    // getOrCreateAwarenessStore() is safe — returns existing store if window key missing
    const store = getOrCreateAwarenessStore();
    if (!store) return undefined;

    const state = store.getState();

    // Capture snapshot (includes any flushed writes)
    const awareness = {
      navigation: { ...state.navigation },
      viewport: {
        renderedComponentType: state.viewport.renderedComponentType,
        surfaceId: state.viewport.surfaceId,
        surfaceType: state.viewport.surfaceType,
        dataContext: { ...state.viewport.dataContext },
      },
      intent: {
        selectedRowCount: state.intent.selectedRowCount,
        selectedRowIds: state.intent.selectedRowIds,
        pinnedRecords: state.intent.pinnedRecords,
        activeRecordId: state.intent.activeRecordId,
        supersetFilters: state.intent.supersetFilters,
        // Include dataResponse only if it was populated (one-shot pull result)
        ...(state.intent.dataResponse ? { dataResponse: state.intent.dataResponse } : {}),
      },
      staleReason: state.staleReason,
      lastUpdatedAt: state.lastUpdatedAt,
    };

    // Consume one-shot DATA_REQUEST responses. Sticky explorer key-facts
    // (requestId key-facts-*) stay until the canvas republishes or clears.
    const dataResponseId = state.intent.dataResponse?.requestId ?? '';
    if (state.intent.dataResponse && !String(dataResponseId).startsWith('key-facts-')) {
      state.clearDataResponse();
    }

    if (process.env.NODE_ENV !== 'production') {
      console.log('[ESA awareness] snapshot sent to agent ↓', awareness);
    }

    return awareness;
  } catch {
    // Defensive: never let awareness errors break the converse flow
    return undefined;
  }
}

/**
 * Derive DESO UI-artefact wire payload from canvas awareness so local ESA/DESO
 * can ground answers on the active surface (filters, selection, solution).
 * Skipped when there is no identifiable surface/canvas.
 */
function buildArtefactsFromAwareness(
  awareness: Record<string, unknown> | undefined,
): Array<Record<string, unknown>> | undefined {
  if (!awareness) return undefined;

  const navigation = (awareness.navigation ?? {}) as Record<string, unknown>;
  const viewport = (awareness.viewport ?? {}) as Record<string, unknown>;
  const intent = (awareness.intent ?? {}) as Record<string, unknown>;
  const dataContext = (viewport.dataContext ?? {}) as Record<string, unknown>;

  const surfaceId = String(viewport.surfaceId || navigation.canvasUkey || '').trim();
  const canvasUkey = String(navigation.canvasUkey || '').trim();
  const solutionId = String(navigation.solutionId || '').trim();
  if (!surfaceId && !canvasUkey && !solutionId) return undefined;

  const selectedFilters: Record<string, unknown> = {};
  const activeFilters = dataContext.activeFilters;
  if (Array.isArray(activeFilters)) {
    for (const raw of activeFilters) {
      if (!raw || typeof raw !== 'object') continue;
      const f = raw as Record<string, unknown>;
      const field = f.field ?? f.key ?? f.name;
      if (field == null || String(field).trim() === '') continue;
      selectedFilters[String(field)] = f.value ?? f.val ?? null;
    }
  }
  const supersetFilters = intent.supersetFilters;
  if (supersetFilters && typeof supersetFilters === 'object' && !Array.isArray(supersetFilters)) {
    Object.assign(selectedFilters, supersetFilters as Record<string, unknown>);
  }

  const selectedDataPoints: Array<Record<string, unknown>> = [];
  const activeRecordId = intent.activeRecordId != null ? String(intent.activeRecordId).trim() : '';
  if (activeRecordId) {
    selectedDataPoints.push({
      label: 'Active record',
      value: activeRecordId,
      series: 'active_record',
    });
  }
  const pinnedRecords = Array.isArray(intent.pinnedRecords) ? intent.pinnedRecords : [];
  for (const pinned of pinnedRecords.slice(0, 10)) {
    if (!pinned || typeof pinned !== 'object') continue;
    const p = pinned as Record<string, unknown>;
    const recordId = String(p.recordId ?? '').trim();
    if (!recordId) continue;
    selectedDataPoints.push({
      label: String(p.label ?? recordId),
      value: recordId,
      series: 'pinned_record',
    });
  }
  const selectedRowIds = intent.selectedRowIds;
  if (Array.isArray(selectedRowIds)) {
    for (const id of selectedRowIds.slice(0, 20)) {
      selectedDataPoints.push({ label: String(id), value: id, series: 'selected_row' });
    }
  }
  const dataResponse = intent.dataResponse as { rows?: unknown[] } | undefined;
  if (Array.isArray(dataResponse?.rows)) {
    for (const row of dataResponse.rows.slice(0, 20)) {
      if (!row || typeof row !== 'object') continue;
      const r = row as Record<string, unknown>;
      const label = String(r.label ?? r.name ?? r.id ?? 'row');
      selectedDataPoints.push({
        label,
        value: r.value ?? r.metric ?? r.count ?? r,
        series: String(r.series ?? 'data_response'),
      });
    }
  }

  const surfaceType = String(viewport.surfaceType || '').toLowerCase();
  const componentType = String(viewport.renderedComponentType || '').toLowerCase();
  let artefactType = 'dashboard';
  if (componentType.includes('chart') || surfaceType.includes('chart')) artefactType = 'chart';
  else if (componentType.includes('table') || surfaceType.includes('table')) artefactType = 'table';
  else if (
    componentType.includes('card') ||
    surfaceType.includes('card') ||
    activeRecordId ||
    selectedDataPoints.some(p => p.series === 'key_facts')
  ) {
    artefactType = 'card';
  }

  const titleFromFacts = selectedDataPoints.find(
    p => p.series === 'key_facts' && String(p.label).toLowerCase() === 'product',
  );
  const title =
    (titleFromFacts && String(titleFromFacts.value)) ||
    (pinnedRecords.find(
      (p: { recordId?: string; label?: string }) => p.recordId === activeRecordId,
    ) as { label?: string } | undefined)?.label ||
    canvasUkey ||
    solutionId ||
    surfaceId ||
    'Active canvas';

  return [
    {
      artefact_id: activeRecordId || surfaceId || canvasUkey || solutionId,
      artefact_type: artefactType,
      title,
      source: solutionId || null,
      selected_filters: selectedFilters,
      selected_data_points: selectedDataPoints,
      metadata: {
        canvas_ukey: canvasUkey || null,
        surface_id: surfaceId || null,
        surface_type: viewport.surfaceType ?? null,
        rendered_component_type: viewport.renderedComponentType ?? null,
        selected_row_count: intent.selectedRowCount ?? 0,
        active_record_id: activeRecordId || null,
        from_canvas_awareness: true,
      },
    },
  ];
}

export interface UseEsaConverseReturn {
  /**
   * Send a user message to the active ESA conversation.
   * @param text         The message text.
   * @param extraContext Optional key-value pairs merged into the `context` field
   *                     of the converse request alongside `canvas_awareness`.
   *                     Example: `{ solution_run_context: snapshot }`.
   */
  sendMessage(text: string, extraContext?: Record<string, unknown>): Promise<void>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Builds a user ESACacheMessage from a text string and any ready attachments.
 * Attachments are embedded in message.attachments so bubbles can render chips.
 */
function buildUserMessage(text: string, threadId: string, attachments: MessageAttachmentRef[]): ESACacheMessage {
  return {
    id: `usr-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    conversationId: threadId,
    parentMessageId: null,
    initiator: 'user',
    messageType: 'text',
    message: {
      text,
      ...(attachments.length > 0 ? { attachments } : {}),
    },
    hasAttachments: attachments.length > 0,
    storedFileUkey: attachments.length === 1 ? attachments[0].stored_file_ukey : null,
    createdAt: new Date().toISOString(),
  };
}

/**
 * Builds an agent ESACacheMessage from a ConverseResponse.
 * Detects message type from metadata.response_type if present.
 */
function buildAgentMessage(
  responseMessage: string,
  conversationId: string,
  metadata: Record<string, unknown>
): ESACacheMessage {
  const responseType = metadata.response_type as string | undefined;
  const isJson = responseType && responseType !== 'direct_answer';
  const messageType: ESACacheMessage['messageType'] = isJson ? 'json' : 'text';

  const msgContent: Record<string, unknown> = isJson
    ? { response_type: responseType, text: responseMessage, ...metadata }
    : {
        text: responseMessage,
        ...(metadata.ui_directives ? { ui_directives: metadata.ui_directives } : {}),
        // PRD-050: forwarded parallel to ui_directives — blocks commonly arrive on
        // direct_answer (non-JSON) responses, so this must not be gated on isJson.
        ...(metadata.blocks ? { blocks: metadata.blocks } : {}),
        // DESO feat-112: open ask chips — keep on content so bubble render need not
        // dig only into the metadata bag (history/json paths already spread metadata).
        ...(metadata.open_commitment ? { open_commitment: metadata.open_commitment } : {}),
      };

  return {
    id: (metadata.last_message_id as string) ?? `agt-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    conversationId,
    parentMessageId: null,
    initiator: 'system',
    messageType,
    message: msgContent,
    metadata,
    hasAttachments: false,
    storedFileUkey: null,
    createdAt: new Date().toISOString(),
  };
}

const PLACEHOLDER_CONVERSATION_TITLES = new Set(['new conversation', 'untitled conversation']);

/** Auto-generated title from converse metadata; skips placeholder defaults. */
function extractAutoConversationTitle(
  metadata: Record<string, unknown> | undefined,
): string | null {
  const raw = metadata?.title;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed || PLACEHOLDER_CONVERSATION_TITLES.has(trimmed.toLowerCase())) {
    return null;
  }
  return trimmed;
}

export function useEsaConverse(): UseEsaConverseReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionIdRef = useRef<string | undefined>(undefined);

  const store = useEsaDomainStore;

  // Keep sessionIdRef in sync with the active thread at all times.
  // - null activeThreadId  → undefined ref  → sendMessage() will create a new conversation
  // - existing thread ID   → ref is pre-set  → sendMessage() resumes that conversation
  //   without ever calling createConversation() again.
  const activeThreadId = useEsaDomainStore(s => s.activeThreadId);
  useEffect(() => {
    sessionIdRef.current = activeThreadId ?? undefined;
  }, [activeThreadId]);

  const sendMessage = useCallback(async (text: string, extraContext?: Record<string, unknown>): Promise<void> => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;

    const cache = getMessageCache();
    const isHilReply = Boolean(extraContext?.hil_id);
    const draftConversationId = sessionIdRef.current;
    const wireText =
      isHilReply || !draftConversationId
        ? trimmed
        : resolveComposerTextForOpenCommitment(
            trimmed,
            cache.getSnapshot(draftConversationId),
          );

    setIsLoading(true);
    setError(null);
    // Mint turn correlator before opening status SSE (feat-80).
    const clientSeq = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    store.getState().setActiveConverseClientSeq(clientSeq);
    store.getState().setStreamStatus('connecting');
    store.getState().setComposerDraft('');

    try {
      const shell = useShellStore.getState();

      // Capture attachments synchronously BEFORE any await — ESAComposer calls
      // clearAttachments() immediately after firing onSend, so reading the store
      // after the first await would return an empty array on new conversations.
      const rawAttachments = useEsaAttachmentStore.getState().getReadyAttachments();
      const attachmentRefs: MessageAttachmentRef[] = rawAttachments.map(a => ({
        stored_file_ukey: a.file_id,
        file_name: a.file_name,
        content_type: a.content_type,
      }));
      const attachmentContext = rawAttachments.length > 0
        ? {
            attachments: rawAttachments.map(a => ({
              file_id: a.file_id,
              file_name: a.file_name,
              content_type: a.content_type,
            })),
          }
        : {};

      let conversationId: string;

      if (!sessionIdRef.current) {
        // ── New conversation: optimistic display + parallel server create ──
        // Assign a provisional ID immediately so the user message can be
        // shown in the chat view without waiting for the POST /conversations
        // round-trip. The provisional thread is rekeyed to the real ID once
        // the server responds (before converse() is called).
        const provisionalId = `prov-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        sessionIdRef.current = provisionalId;
        store.getState().setActiveThread(provisionalId);

        // Show user message immediately — no server wait
        const userMsg = buildUserMessage(wireText, provisionalId, attachmentRefs);
        cache.addMessage(provisionalId, userMsg);

        const created = await createConversation('New conversation', {
          tenantId: shell.tenant?.id,
          userId: shell.user?.id,
        });
        const realId = created.id;

        // Rekey: move provisional messages to the real conversation ID,
        // then switch the active thread atomically so the view doesn't flash.
        // Mark history loaded (default): this thread was just created — there is
        // no useful server history yet, and fetching would risk duplicating the
        // optimistic usr-* rows once the server assigns real ids.
        const provisionalMsgs = cache.getSnapshot(provisionalId);
        cache.setThread(
          realId,
          provisionalMsgs.map(m => ({ ...m, conversationId: realId })),
        );
        cache.evict(provisionalId);

        sessionIdRef.current = realId;
        store.getState().setActiveThread(realId);
        store.getState().upsertThread({
          id: realId,
          title: null,
          lastUpdatedAt: Date.now(),
          hasActiveHITL: false,
          status: 'active',
        });

        conversationId = realId;
      } else {
        conversationId = sessionIdRef.current;

        // Existing conversation: show user message optimistically with attachments
        const userMsg = buildUserMessage(wireText, conversationId, attachmentRefs);
        cache.addMessage(conversationId, userMsg);
      }

      // Mid-turn status SSE + flash card (feat-80): mark streaming once the
      // real conversation id is known so ConverseStatusCard can open (skips prov-*).
      store.getState().setStreamStatus('streaming');

      // ── converse with the established session_id ─────────────────────────
      const canvasAwareness = buildAwarenessContext();
      const artefacts = buildArtefactsFromAwareness(canvasAwareness);
      // clientSeq minted at send start; agents echo it on status events.

      const response = await converse({
        message: wireText,
        session_id: conversationId,
        tenant_ukey: shell.tenant?.id,
        initiator_id: shell.user?.id,
        context: {
          ...(canvasAwareness ? { canvas_awareness: canvasAwareness } : {}),
          ...(artefacts ? { artefacts } : {}),
          ...attachmentContext,
          client_seq: clientSeq,
          conversation_id: conversationId,
          ...(extraContext ?? {}),
        },
      });

      // Backend may return a normalised conversation_id — use it if present,
      // otherwise the id we used for the request is already correct.
      const serverConvId = (response.metadata?.conversation_id as string | undefined) ?? conversationId;

      // Update session ref with whatever the server confirmed
      sessionIdRef.current = response.session_id ?? serverConvId;

      // Declared here (before the rekey block) so the HIL guard is available early.
      // isHilReply set at send start for wire-text resolution skip.

      // If the server returned a different id (shouldn't happen with explicit
      // creation, but handle it defensively), rekey the cache.
      // Skip entirely for HIL replies — HIL conversations must not enter the store.
      if (serverConvId !== conversationId && !isHilReply) {
        const msgs = cache.getSnapshot(conversationId);
        cache.setThread(
          serverConvId,
          msgs.map(m => ({ ...m, conversationId: serverConvId })),
        );
        cache.evict(conversationId);
        store.getState().setActiveThread(serverConvId);
        store.getState().upsertThread({
          id: serverConvId,
          title: null,
          lastUpdatedAt: Date.now(),
          hasActiveHITL: false,
          status: 'active',
        });
      }

      // Build and store agent message — merge top-level ui_directives (and, PRD-050,
      // blocks) into the metadata bag so buildAgentMessage can store them in the
      // message content.
      const responseMetadata = {
        ...(response.metadata ?? {}),
        ...(response.ui_directives ? { ui_directives: response.ui_directives } : {}),
        ...(response.blocks ? { blocks: response.blocks } : {}),
      };

      // Detect HITL in response metadata early — used to gate cache/thread writes below.
      const responseType = response.metadata?.response_type as string | undefined;
      const isHilEscalation = responseType === 'hil_escalation';
      // isHilReply already declared above (before the rekey block).

      // HIL escalation responses must not appear in the main conversation thread
      // (agent saying "I need human input" — skip the cache write so it's invisible
      // in the main chat). HIL reply messages (isHilReply) DO go to the HIL
      // conversation cache, which ResolvingView reads — they must be kept.
      if (!isHilEscalation) {
        const agentMsg = buildAgentMessage(
          response.message,
          serverConvId,
          responseMetadata,
        );
        cache.addMessage(serverConvId, agentMsg);
      }

      const autoTitle = extractAutoConversationTitle(responseMetadata);

      // Only update the thread list for non-HIL messages to prevent HIL
      // conversations from appearing as entries in the main thread sidebar.
      // When a HIL escalation is the response, preserve the existing conversation
      // title (pass null so upsertThread's merge keeps whatever title it already
      // has) — the HIL question must not overwrite the main conversation title.
      if (!isHilReply) {
        store.getState().upsertThread({
          id: serverConvId,
          title: isHilEscalation ? null : autoTitle,
          lastUpdatedAt: Date.now(),
          hasActiveHITL: isHilEscalation,
          status: 'active',
        });
      }

      // Dispatch any canvas instruction returned in response metadata
      if (response.metadata) {
        dispatchInstruction(response.metadata as Record<string, unknown>);
      }

      // Chat plan-confirm starts a run without Invoke overlay / ResolvingView.
      syncCanvasAfterConverse(responseMetadata, {
        openDispatchedRun: !isHilReply && !isHilEscalation,
      });

      if (isHilEscalation) {
        const hilId = response.metadata?.hil_id as string | undefined;
        if (hilId) {
          store.getState().addPendingHITL({
            id: hilId,
            agentRunId: (response.metadata?.agent_run_id as string) ?? '',
            agentUkey: (response.metadata?.agent_ukey as string) ?? '',
            question: (response.metadata?.hil_question as string) ?? response.message,
            requestType: (response.metadata?.request_type as string) ?? null,
            priority: 'NORMAL',
            status: 'PENDING',
            conversationId: serverConvId,
            createdAt: new Date().toISOString(),
          });
        }
      }

      // Pin active thread in cache (never evict while active)
      if (!isHilReply) cache.setPinnedThread(serverConvId);

      store.getState().setStreamStatus('idle');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'ESA is unavailable right now.';
      setError(message);
      store.getState().setStreamStatus('error');
      store.getState().setStreamError(message);
      // streamError is not rendered as a chat bubble — surface the failure in-thread
      // so the user sees ESA/DESO timeout/unavailable copy next to their message.
      const failedConversationId = sessionIdRef.current;
      if (failedConversationId) {
        cache.addMessage(
          failedConversationId,
          buildAgentMessage(message, failedConversationId, {
            response_type: 'direct_answer',
            error: true,
          }),
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, store]);

  return { sendMessage, isLoading, error };
}
